// A parent's "This week" report for one child, built from what the app already keeps per child profile:
//   activity  — lumio_activity[_<profile>]: one entry per finished practice/exam session { ts, subject, correct, total, mins? }
//   glowup    — lumio_glowup[_<profile>]: the child's saved mistakes and how far each has come (src/lib/glowUp.js)
//   progress  — lumio_progress_v2[_<profile>]: running totals per exam-year-subject, with per-topic counts
// All of it follows the account (src/lib/accountSync.js), so the report is the same on any device.

const DAY = 864e5;
const pct = (c, t) => (t > 0 ? Math.round((c / t) * 100) : null);
const dayKey = (ts) => { const d = new Date(ts); return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`; };

function summarise(entries) {
  const questions = entries.reduce((s, e) => s + (e.total || 0), 0);
  const correct = entries.reduce((s, e) => s + (e.correct || 0), 0);
  const minutes = entries.reduce((s, e) => s + (Number.isFinite(e.mins) ? e.mins : 0), 0);
  return { sessions: entries.length, questions, correct, accuracy: pct(correct, questions), minutes };
}

/** The daily goal: questions answered in finished practice or exam sessions on one calendar day. */
export const DAILY_GOAL = 10;

function questionsByDay(activity) {
  const by = new Map();
  for (const e of activity) if (e && Number.isFinite(e.ts)) by.set(dayKey(e.ts), (by.get(dayKey(e.ts)) || 0) + (e.total || 0));
  return by;
}

/** Days in a row, ending today or yesterday, on which the daily goal was met (minimum 1 = any practice at all). */
export function streakDays(activity, now = Date.now(), minimum = DAILY_GOAL) {
  const by = questionsByDay(activity);
  const met = (t) => (by.get(dayKey(t)) || 0) >= minimum;
  // step back by calendar day, not 24 h, so a daylight-saving change can't skip or repeat a day
  const d = new Date(now);
  if (!met(now)) d.setDate(d.getDate() - 1);              // yesterday still counts as "on a streak" until today ends
  let n = 0;
  for (; met(d.getTime()); d.setDate(d.getDate() - 1)) n++;
  return n;
}

/** Today's progress towards the goal, and the streak, for the child's home screen. */
export function dailyGoal(activity = [], now = Date.now(), goal = DAILY_GOAL) {
  const today = questionsByDay(activity).get(dayKey(now)) || 0;
  return { today, goal, met: today >= goal, streak: streakDays(activity, now, goal) };
}

export function buildWeeklyReport({ activity = [], glowup = null, progress = {}, now = Date.now(), subjectLabel = (s) => s } = {}) {
  const weekStart = now - 7 * DAY, prevStart = now - 14 * DAY;
  const valid = activity.filter((e) => e && Number.isFinite(e.ts));
  const week = valid.filter((e) => e.ts > weekStart && e.ts <= now);
  const prev = valid.filter((e) => e.ts > prevStart && e.ts <= weekStart);
  const totals = summarise(week);
  const activeDays = new Set(week.map((e) => dayKey(e.ts))).size;

  // by subject, with the change in accuracy against the week before
  const subjects = [...new Set(week.map((e) => e.subject))];
  const bySubject = subjects.map((s) => {
    const cur = summarise(week.filter((e) => e.subject === s));
    const before = summarise(prev.filter((e) => e.subject === s));
    return { subject: s, label: subjectLabel(s), questions: cur.questions, accuracy: cur.accuracy,
      change: cur.accuracy != null && before.accuracy != null ? cur.accuracy - before.accuracy : null };
  }).sort((a, b) => b.questions - a.questions);

  // Glow Up: mistakes saved, fixed and mastered this week, and what is waiting
  const items = glowup && glowup.items ? Object.values(glowup.items) : [];
  const glow = {
    saved: items.filter((i) => i.added > weekStart).length,
    fixed: items.filter((i) => i.stage >= 1 && i.stage < 3 && i.updated > weekStart).length,
    shining: items.filter((i) => i.stage === 3 && i.shinedAt > weekStart).length,
    waiting: items.filter((i) => i.stage < 3).length,
  };

  // focus: the weakest topics with enough attempts to mean something (running totals, not just this week)
  const topics = [];
  for (const [key, rec] of Object.entries(progress || {})) {
    for (const [topic, t] of Object.entries((rec && rec.topics) || {})) {
      if ((t.total || 0) >= 6) topics.push({ topic, subject: key.split('-').pop(), attempts: t.total, accuracy: pct(t.correct, t.total) });
    }
  }
  const focus = topics.filter((t) => t.accuracy < 70).sort((a, b) => a.accuracy - b.accuracy || b.attempts - a.attempts).slice(0, 3);
  const strengths = topics.filter((t) => t.accuracy >= 85).sort((a, b) => b.attempts - a.attempts).slice(0, 2);

  const streak = streakDays(valid, now);
  let headline;
  if (!totals.sessions) headline = 'No practice yet this week — a 10-minute session is a great way to start.';
  else if (activeDays >= 5) headline = `A brilliant week: practice on ${activeDays} of the last 7 days.`;
  else if (totals.accuracy != null && totals.accuracy >= 80) headline = `Strong accuracy this week: ${totals.accuracy}% across ${totals.questions} questions.`;
  else headline = `${totals.questions} questions over ${activeDays} day${activeDays === 1 ? '' : 's'} this week — steady progress.`;

  let next;
  if (glow.waiting > 0) next = `${glow.waiting} mistake${glow.waiting === 1 ? ' is' : 's are'} waiting in Glow Up — fixing them is the quickest win.`;
  else if (focus.length) next = `Spend a session on ${focus[0].topic} (${focus[0].accuracy}% so far).`;
  else if (!totals.sessions) next = 'Try one short practice session in any subject.';
  else next = 'Keep the routine going — aim for a little practice most days.';

  return { from: weekStart, to: now, ...totals, activeDays, streak, bySubject, glow, focus, strengths, headline, next };
}
